import {
  ARRIVAL_PATHS,
  registerWidgetContext,
  resolveArrivalPath,
  resolveHostDomain,
  resolveIsInternal,
  widgetEnvironmentProperties,
} from './context';
import { analyticsState, consumeAutoAdvance, setStepContext } from './state';
import { DIRECTIONS, firstStepId, stepIndex } from './steps';
import { trackStepCompleted, trackStepReached, trackWidgetOpened } from './events';

export * from './events';
export * from './steps';
export { ARRIVAL_PATHS, registerWidgetContext } from './context';
export { noteAutoAdvance, noteSessionResumed, resetAnalyticsState } from './state';

// Runs before the calendar's settings are fetched, so a widget that fails to
// load still reports WHERE it failed and on whose page. Everything here comes
// from the URL and the frame, which are known on the first line of execution.
export const initWidgetAnalytics = ({ calendarId, variant, searchParams, hasRwgToken }) => {
  const arrivalPath = resolveArrivalPath(searchParams, hasRwgToken);

  registerWidgetContext({
    calendar_id: calendarId,
    arrival_path: arrivalPath,
    host_domain: resolveHostDomain(),
    variant,
    // Settled from the URL alone for employee previews and our own demo pages;
    // a Cinch-owned CALENDAR can only be known once its settings arrive, which
    // can flip this to true below.
    is_internal: resolveIsInternal({ arrivalPath }),
    ...widgetEnvironmentProperties(),
  });

  return { arrivalPath };
};

// Runs once the calendar's public settings land. Both of these change what the
// consumer is shown, so they have to be registered before any step event: the
// server decides whether the calendar is Cinch's own, and phone verification
// plus the location skip decide every step's index.
export const applyCalendarAnalyticsContext = ({ settings, arrivalPath, locationStepSkipped }) => {
  const phoneVerificationRequired = !!settings?.validate_phone;

  setStepContext({ phoneVerificationRequired, locationStepSkipped });
  registerWidgetContext({
    phone_verification_required: phoneVerificationRequired,
    location_step_skipped: !!locationStepSkipped,
    is_internal: resolveIsInternal({ calendarIsInternal: settings?.is_internal, arrivalPath }),
  });
};

// The server issues this when the booking session is created; it is how a
// visit's events are matched to each other and to the widget's own visit record.
export const setBookingSessionId = (bookingSessionId) =>
  registerWidgetContext({ booking_session_id: bookingSessionId });

// `booking_widget_opened` needs two things that arrive in either order: the
// widget being on screen, and which step it landed on. An embedded widget loads
// hidden with the host page, so the embed script has to say when it is shown; a
// direct link, a Reserve with Google link and a preview have no hidden phase, so
// arriving is already being shown. The step comes later, from whichever screen
// renders first. This waits for both and fires once.
export const createOpenTracker = ({ arrivalPath, locationCount }) => {
  let isShown = arrivalPath !== ARRIVAL_PATHS.EMBED;
  let landing = null;
  let fired = false;
  const shownListeners = new Set();

  const fireIfReady = () => {
    if (fired || !isShown || !landing) return;
    fired = true;
    trackWidgetOpened({ ...landing, locationCount });
  };

  const onMessage = (event) => {
    if (event?.data?.type !== 'WIDGET_SHOWN') return;
    if (isShown) return;
    isShown = true;
    fireIfReady();
    shownListeners.forEach((listener) => listener());
    shownListeners.clear();
  };
  window.addEventListener('message', onMessage);

  // Announce the listener. The embed script shows the widget by creating the
  // iframe and posting WIDGET_SHOWN straight away, which is well before this
  // module runs — that message is already gone. Asking is what covers a widget
  // that was visible from the moment it loaded; the listener above covers one
  // that was embedded hidden and shown later.
  if (!isShown) {
    try {
      window.parent.postMessage({ type: 'WIDGET_READY' }, '*');
    } catch (e) {
      // Nothing to answer us: treat it as not shown rather than failing open.
    }
  }

  return {
    reportLanding: (properties) => {
      if (landing) return;
      landing = properties;
      fireIfReady();
    },
    isShown: () => isShown,
    // Resolves immediately when the widget is already on screen, so a caller
    // never has to special-case the non-embed paths.
    onShown: (listener) => {
      if (isShown) {
        listener();
        return () => {};
      }
      shownListeners.add(listener);
      return () => shownListeners.delete(listener);
    },
    dispose: () => {
      shownListeners.clear();
      window.removeEventListener('message', onMessage);
    },
  };
};

// The tracker is started by the provider that fetches the calendar and fed by
// the component that renders the first screen. Held here rather than passed
// between them: CalendarContext sits above the step state, so threading it
// through would put an analytics-only field on the booking widget's context.
let openTracker = null;

export const startOpenTracking = ({ arrivalPath, locationCount }) => {
  openTracker?.dispose();
  openTracker = createOpenTracker({ arrivalPath, locationCount });
};

export const reportWidgetLanding = (properties) => openTracker?.reportLanding(properties);

// Step events are held until the widget is on screen. An embed that is created
// up front — `SchedulerWidget.init()`, or `data-auto-init` — boots the whole app
// hidden on every page view of the host site, and reporting the step it renders
// behind the scenes would put every one of those page views at step 1 of the
// funnel and turn each into an event (CIN-7080).
export const isWidgetShown = () => !!openTracker?.isShown();

export const onWidgetShown = (listener) => openTracker?.onShown(listener) ?? (() => {});

export const stopOpenTracking = () => {
  openTracker?.dispose();
  openTracker = null;
};

// Watches the step the widget is showing and turns each change into a pair of
// events. Driven by the rendered step rather than by the handful of places that
// call setCurrentStep: the session restore, three auto-advance effects and the
// deep-link resolver all move the consumer, and a funnel that missed any of
// them would read as a drop-off that never happened. Those same callers arm
// noteAutoAdvance() so the move they made is told apart from a click.
export const createStepTracker = () => {
  let previousStepId = null;

  const directionOf = (stepId) => {
    const options = analyticsState();
    // Always consumed, so an arm that never produced a report cannot leak into
    // a later move the consumer actually made.
    const autoAdvanced = consumeAutoAdvance();
    if (previousStepId === null) {
      if (stepId === firstStepId(options)) return DIRECTIONS.FORWARD;
      return options.isResumedSession ? DIRECTIONS.RESUME : DIRECTIONS.DEEP_LINK;
    }
    const from = stepIndex(previousStepId, options);
    const to = stepIndex(stepId, options);
    if (from === null || to === null) return DIRECTIONS.FORWARD;
    if (to <= from) return DIRECTIONS.BACK;
    return autoAdvanced ? DIRECTIONS.AUTO_SKIP : DIRECTIONS.FORWARD;
  };

  return {
    report: ({ stepId, detailsFor }) => {
      if (!stepId || stepId === previousStepId) return;

      const direction = directionOf(stepId);
      const completedStepId = previousStepId;
      previousStepId = stepId;

      // The step being left only counts as completed when the consumer moved
      // ON from it; stepping back is not undoing a completion. An auto_skip is
      // still a move on — withholding the completion would invent a drop-off at
      // the very screen the widget skipped for them.
      if (completedStepId && (direction === DIRECTIONS.FORWARD || direction === DIRECTIONS.AUTO_SKIP)) {
        trackStepCompleted({ stepId: completedStepId, ...(detailsFor?.(completedStepId) || {}) });
      }
      trackStepReached({ stepId, direction });
    },
  };
};
